import { z } from "zod";

/** Body cap enforced at the DTO boundary and mirrored by the composer's maxLength. */
export const ARENA_SUPPORT_MAX_TEXT_LENGTH = 2000;
/** Messages per history page. Chatwoot ships 20, Stream 25, Discord 50. */
export const ARENA_SUPPORT_PAGE_SIZE = 25;

export const ArenaSupportTopicSchema = z.enum(["DEPOSITS", "PAYOUTS", "WALLET", "RESULTS", "RULES", "BUG", "OTHER"]);
export type ArenaSupportTopic = z.infer<typeof ArenaSupportTopicSchema>;

/** Where a message sent before any topic was tapped lands — the picker is a shortcut, never a gate. */
export const ARENA_SUPPORT_UNSPECIFIED_TOPIC = "OTHER" as const satisfies ArenaSupportTopic;

export const ArenaSupportStatusSchema = z.enum(["OPEN", "RESOLVED", "CLOSED"]);
export type ArenaSupportStatus = z.infer<typeof ArenaSupportStatusSchema>;

/**
 * No SYSTEM: the topic echo and resolved notice render client-side, never persisted.
 * AGENT is a Cede team member (ops), AI is the Cede Support Agent, shown as "Cede AI".
 */
export const ArenaSupportAuthorRoleSchema = z.enum(["USER", "AGENT", "AI"]);
export type ArenaSupportAuthorRole = z.infer<typeof ArenaSupportAuthorRoleSchema>;

/** Who answers the conversation. HUMAN: the AI stays silent until resolved or handed back. */
export const ArenaSupportHandledBySchema = z.enum(["AI", "HUMAN"]);
export type ArenaSupportHandledBy = z.infer<typeof ArenaSupportHandledBySchema>;

/** What an AI message is. Only ANSWER counts towards time to first useful response. */
export const ArenaSupportAiMessageKindSchema = z.enum([
  "ANSWER",
  "REPHRASE_REQUEST",
  "HANDOFF_ACK",
  "GREETING",
  "THANKS",
  "FEEDBACK_THANKS",
  "BUG_REPORT_ACK",
  /** The team already has the user's problem: posted once when they write about it again while waiting. */
  "TEAM_WAIT_NOTE",
  /** The KB could not answer a problem: the AI asks what happens exactly before reporting it. */
  "DETAILS_REQUEST",
]);
export type ArenaSupportAiMessageKind = z.infer<typeof ArenaSupportAiMessageKindSchema>;

export const ArenaSupportMessageReviewVerdictSchema = z.enum(["CORRECT", "WRONG"]);
export type ArenaSupportMessageReviewVerdict = z.infer<typeof ArenaSupportMessageReviewVerdictSchema>;

/** Who wrote the replies of a conversation; NONE when nobody answered. */
export const ArenaSupportAnsweredBySchema = z.enum(["AI_ONLY", "HUMAN_ONLY", "MIXED", "NONE"]);
export type ArenaSupportAnsweredBy = z.infer<typeof ArenaSupportAnsweredBySchema>;

export const ARENA_SUPPORT_RATING_MIN = 1;
export const ARENA_SUPPORT_RATING_MAX = 5;
export const ARENA_SUPPORT_RATING_COMMENT_MAX_LENGTH = 1000;

/**
 * The picker buttons. Order is the render order; deposits first per the ticket's
 * stated focus. Excludes OTHER — it is assigned, never offered.
 */
export const ARENA_SUPPORT_TOPICS = [
  { key: "DEPOSITS", emoji: "💰", label: "Deposits & buy-ins" },
  { key: "PAYOUTS", emoji: "🏆", label: "Payouts & prizes" },
  { key: "WALLET", emoji: "🔑", label: "Wallet & login" },
  { key: "RESULTS", emoji: "📊", label: "My competition & results" },
  { key: "RULES", emoji: "📖", label: "Rules & fees" },
  { key: "BUG", emoji: "🐛", label: "Report a bug or share feedback" },
] as const satisfies ReadonlyArray<{ key: ArenaSupportTopic; emoji: string; label: string }>;

/** Labels for every stored topic, OTHER included. Display side only; render the picker from `ARENA_SUPPORT_TOPICS`. */
export const ARENA_SUPPORT_TOPIC_META = [
  ...ARENA_SUPPORT_TOPICS,
  { key: ARENA_SUPPORT_UNSPECIFIED_TOPIC, emoji: "🗂", label: "Other" },
] as const satisfies ReadonlyArray<{ key: ArenaSupportTopic; emoji: string; label: string }>;

/** Fixed per-topic acknowledgement. Client-rendered — deliberately not a stored message. */
export const ARENA_SUPPORT_TOPIC_ECHO =
  "Got it. Tell us what happened: the more detail the better. If it's a transfer, the tx hash helps a lot.";

export const ArenaSupportMessageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  authorRole: ArenaSupportAuthorRoleSchema,
  /** Null for AGENT and AI rows. */
  hubUserId: z.number().int().nullable(),
  body: z.string(),
  /** Epoch ms. */
  createdAt: z.number(),
});
export type ArenaSupportMessageDto = z.infer<typeof ArenaSupportMessageSchema>;

export const ArenaSupportConversationSchema = z.object({
  id: z.string(),
  topic: ArenaSupportTopicSchema,
  status: ArenaSupportStatusSchema,
  lastMessageRole: ArenaSupportAuthorRoleSchema,
  lastMessageAt: z.number(),
  lastSeenAt: z.number().nullable(),
  resolvedBy: ArenaSupportAuthorRoleSchema.nullable(),
  resolvedAt: z.number().nullable(),
  /** 1–5, null until the user rates the resolved conversation. */
  rating: z.number().int().min(ARENA_SUPPORT_RATING_MIN).max(ARENA_SUPPORT_RATING_MAX).nullable(),
});
export type ArenaSupportConversationDto = z.infer<typeof ArenaSupportConversationSchema>;

/**
 * What the widget needs to render one continuous transcript: the live thread (if
 * any) plus the recently-resolved ones, so it can draw a divider where each
 * ended. `active` is null once a conversation is resolved — RESOLVED is terminal,
 * so the picker returns and the next message opens a new conversation.
 */
export const GetArenaSupportConversationResponseSchema = z.object({
  active: ArenaSupportConversationSchema.nullable(),
  recent: z.array(ArenaSupportConversationSchema),
});
export type GetArenaSupportConversationResponse = z.infer<typeof GetArenaSupportConversationResponseSchema>;
export type ArenaSupportConversationStateDto = GetArenaSupportConversationResponse;

export const GetArenaSupportMessagesResponseSchema = z.object({
  messages: z.array(ArenaSupportMessageSchema),
  hasMore: z.boolean(),
});
export type GetArenaSupportMessagesResponse = z.infer<typeof GetArenaSupportMessagesResponseSchema>;

const nonBlankBody = z
  .string()
  .min(1)
  .max(ARENA_SUPPORT_MAX_TEXT_LENGTH)
  .refine((value) => value.trim().length > 0, { message: "Message body cannot be blank" });

/** Opens a conversation (or, if one is already active, appends to it) with its first message. */
export const OpenArenaSupportConversationRequestSchema = z.object({
  topic: ArenaSupportTopicSchema,
  originRoute: z.string().min(1).max(512),
  /** Client-generated idempotency key, unique per conversation. */
  clientId: z.string().min(1).max(64),
  body: nonBlankBody,
});

export const SendArenaSupportMessageRequestSchema = z.object({
  conversationId: z.string().min(1),
  /** Client-generated idempotency key, unique per conversation. */
  clientId: z.string().min(1).max(64),
  body: nonBlankBody,
});

/** Query for `GET /v1/arena-support-chat/messages` — cursor pagination, newest page first. */
export const GetArenaSupportMessagesQuerySchema = z.object({
  /** Omitted on the user path — the widget reads its whole transcript. Required by the SSE route. */
  conversationId: z.string().min(1).optional(),
  before: z.string().min(1).optional(),
  limit: z.coerce.number().int().positive().optional(),
});

/** Body for both `POST /v1/arena-support-chat/seen` and `.../resolve`. */
export const ResolveArenaSupportConversationRequestSchema = z.object({
  conversationId: z.string().min(1),
});

/** Body for `POST /v1/arena-support-chat/rate`. Only a RESOLVED conversation can be rated, once. */
export const RateArenaSupportConversationRequestSchema = z.object({
  conversationId: z.string().min(1),
  rating: z.number().int().min(ARENA_SUPPORT_RATING_MIN).max(ARENA_SUPPORT_RATING_MAX),
  comment: z.string().max(ARENA_SUPPORT_RATING_COMMENT_MAX_LENGTH).optional(),
});
export type RateArenaSupportConversationRequest = z.infer<typeof RateArenaSupportConversationRequestSchema>;

export const ArenaSupportAcceptedResponseSchema = z.object({ accepted: z.boolean() });
export type ArenaSupportAcceptedResponse = z.infer<typeof ArenaSupportAcceptedResponseSchema>;

/**
 * SSE frames. PING is the 20s keepalive (the ALB idle timeout is 60s) and is a
 * real member of the union so a strict client parse never throws on it.
 */
export const ArenaSupportSseEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("SUPPORT_MESSAGE"), payload: z.object({ message: ArenaSupportMessageSchema }) }),
  /** Transient "Cede AI is typing…" — never stored; cleared by the next AI message or `isTyping: false`. */
  z.object({
    type: z.literal("SUPPORT_TYPING"),
    payload: z.object({ conversationId: z.string(), isTyping: z.boolean() }),
  }),
  z.object({ type: z.literal("PING") }),
]);
export type ArenaSupportSseEventDto = z.infer<typeof ArenaSupportSseEventSchema>;

export const CmsArenaSupportConversationSchema = ArenaSupportConversationSchema.extend({
  hubUserId: z.number().int(),
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  accountCreatedAt: z.number(),
  balanceUsdt: z.string().nullable(),
  lastCompetition: z.string().nullable(),
  originRoute: z.string(),
  userAgent: z.string().nullable(),
  lastMessagePreview: z.string(),
  handledBy: ArenaSupportHandledBySchema,
  /** Ops email that took the conversation over; null while the AI handles it. */
  takenOverBy: z.string().nullable(),
  /** Consecutive low-confidence AI runs; the 2nd asks the team to step in. */
  lowConfidenceStreak: z.number().int().nonnegative(),
  /**
   * The AI asked the team to step in (bug report or second miss) and nobody from the team replied yet. The AI keeps
   * answering other topics meanwhile; the conversation stays in "needs reply".
   */
  teamRequestedAt: z.number().nullable(),
  answeredBy: ArenaSupportAnsweredBySchema,
  ratingComment: z.string().nullable(),
});
export type CmsArenaSupportConversationDto = z.infer<typeof CmsArenaSupportConversationSchema>;

export const CmsArenaSupportConversationsResponseSchema = z.object({
  conversations: z.array(CmsArenaSupportConversationSchema),
  hasMore: z.boolean(),
});
export type CmsArenaSupportConversationsResponse = z.infer<typeof CmsArenaSupportConversationsResponseSchema>;

/** "Needs reply" / "waiting on user" are derived from (status, lastMessageRole) — not a status of their own. */
export const CmsArenaSupportConversationStatusFilterSchema = z.enum([
  "NEEDS_REPLY",
  "WAITING_ON_USER",
  "RESOLVED",
  "ALL",
]);
export type CmsArenaSupportConversationStatusFilter = z.infer<typeof CmsArenaSupportConversationStatusFilterSchema>;

/** Query for `GET /v1/cms/arena-support-chat/conversations`. */
export const CmsListArenaSupportConversationsQuerySchema = z.object({
  status: CmsArenaSupportConversationStatusFilterSchema.optional(),
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type CmsListArenaSupportConversationsQuery = z.infer<typeof CmsListArenaSupportConversationsQuerySchema>;

/** Message as ops see it in the CMS thread: author, AI kind, the agent run behind it and the review. */
export const CmsArenaSupportMessageSchema = ArenaSupportMessageSchema.extend({
  agentEmail: z.string().nullable(),
  aiKind: ArenaSupportAiMessageKindSchema.nullable(),
  /** `SupportAgentRun` id (AWS DB) that produced this AI message. */
  agentRunId: z.string().nullable(),
  reviewVerdict: ArenaSupportMessageReviewVerdictSchema.nullable(),
  reviewNote: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.number().nullable(),
});
export type CmsArenaSupportMessageDto = z.infer<typeof CmsArenaSupportMessageSchema>;

export const CmsArenaSupportMessagesResponseSchema = z.object({
  messages: z.array(CmsArenaSupportMessageSchema),
  hasMore: z.boolean(),
});
export type CmsArenaSupportMessagesResponse = z.infer<typeof CmsArenaSupportMessagesResponseSchema>;

/** Body for `POST /v1/cms/arena-support-chat/take-over` and `.../hand-back`. Ops identity from `X-User-Email`. */
export const CmsArenaSupportConversationActionRequestSchema = z.object({
  conversationId: z.string().min(1),
});
export type CmsArenaSupportConversationActionRequest = z.infer<typeof CmsArenaSupportConversationActionRequestSchema>;

export const ARENA_SUPPORT_REVIEW_NOTE_MAX_LENGTH = 1000;

/** Body for `POST /v1/cms/arena-support-chat/review-message`. AI messages only. */
export const CmsReviewArenaSupportMessageRequestSchema = z.object({
  messageId: z.string().min(1),
  verdict: ArenaSupportMessageReviewVerdictSchema,
  note: z.string().max(ARENA_SUPPORT_REVIEW_NOTE_MAX_LENGTH).optional(),
});
export type CmsReviewArenaSupportMessageRequest = z.infer<typeof CmsReviewArenaSupportMessageRequestSchema>;

/** Body for `POST /v1/cms/arena-support-chat/reply`. Agent identity comes from the `X-User-Email` header, never the body. */
export const CmsReplyAsArenaSupportAgentRequestSchema = z.object({
  conversationId: z.string().min(1),
  clientId: z.string().min(1).max(64),
  body: nonBlankBody,
});
export type CmsReplyAsArenaSupportAgentRequest = z.infer<typeof CmsReplyAsArenaSupportAgentRequestSchema>;
